import { useCallback, useEffect, useRef, useState } from 'react';
import { getLeaveApplications } from '../services/leaveService';
import type {
  LeaveApplicationListItem,
  LeaveHistoryFilters,
} from '../types/leave';
import { leaveErrorMessage } from '../utils/leaveErrors';
import { createDefaultLeaveFilters } from '../utils/leaveFilters';

const DEFAULT_PAGE_SIZE = 20;

export function useLeaveApplications() {
  const [items, setItems] = useState<LeaveApplicationListItem[]>([]);
  const [filters, setFiltersState] = useState<LeaveHistoryFilters>(
    createDefaultLeaveFilters(),
  );
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mounted = useRef(true);
  const requestId = useRef(0);
  const loadingMore = useRef(false);
  const pageRef = useRef(1);
  const filtersRef = useRef(filters);

  const fetchPage = useCallback(
    async (nextPage: number, mode: 'replace' | 'append' | 'refresh') => {
      const currentRequest = ++requestId.current;
      if (mode === 'append') {
        if (loadingMore.current) return;
        loadingMore.current = true;
        setIsLoadingMore(true);
      } else if (mode === 'refresh') {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      try {
        const result = await getLeaveApplications({
          page: nextPage,
          pageSize: DEFAULT_PAGE_SIZE,
          filters: filtersRef.current,
        });

        if (!mounted.current || currentRequest !== requestId.current) return;

        setPage(result.page);
        pageRef.current = result.page;
        setHasMore(result.page < result.totalPages);
        setItems(current => {
          if (mode === 'append') {
            const seen = new Set(current.map(item => item.leaveApplicationId));
            const merged = [...current];
            for (const item of result.items) {
              if (!seen.has(item.leaveApplicationId)) merged.push(item);
            }
            return merged;
          }
          return result.items;
        });
      } catch (caught) {
        if (mounted.current && currentRequest === requestId.current) {
          setError(
            leaveErrorMessage(caught, 'Unable to load leave applications.'),
          );
        }
      } finally {
        loadingMore.current = false;
        if (mounted.current && currentRequest === requestId.current) {
          setIsLoading(false);
          setIsRefreshing(false);
          setIsLoadingMore(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const applyFilters = useCallback(
    (next: LeaveHistoryFilters) => {
      setFiltersState(next);
      filtersRef.current = next;
      pageRef.current = 1;
      fetchPage(1, 'replace');
    },
    [fetchPage],
  );

  const resetFilters = useCallback(() => {
    const defaults = createDefaultLeaveFilters();
    setFiltersState(defaults);
    filtersRef.current = defaults;
    pageRef.current = 1;
    fetchPage(1, 'replace');
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore.current || isLoading || isRefreshing) return;
    fetchPage(pageRef.current + 1, 'append');
  }, [fetchPage, hasMore, isLoading, isRefreshing]);

  return {
    items,
    filters,
    page,
    isLoading,
    isRefreshing,
    isLoadingMore,
    hasMore,
    error,
    loadApplications: () => fetchPage(1, 'replace'),
    refreshApplications: () => fetchPage(1, 'refresh'),
    loadMore,
    applyFilters,
    resetFilters,
    retry: () => fetchPage(1, 'replace'),
  };
}
